import React, { useMemo, useCallback } from 'react';
import { Component } from '@/types';
import { shouldBlockSensorControl, useComponentInteraction } from '@/components/shared/rendering/InteractionContextRuntime';
import { useDataTextValue } from '@/hooks/useDataTextValue';
import { useDeviceStatus } from '@/hooks/useDeviceStatus';
import { ChartDataSource } from '@/types/chartDataSource';
import { useHomeAssistantEntityState, haStateToBoolean } from '@/services/homeAssistant/useEntityState';
import { CONTROL_THEME } from './controlThemeTokens';

// ─── Types ───────────────────────────────────────────────────────────────────

interface ToggleThemeConfig {
  trackOnColor: string;
  trackOffColor: string;
  thumbColor: string;
  thumbShadow: string;
  borderRadius: string;
  trackWidth: number;
  trackHeight: number;
  thumbSize: number;
  thumbOffset: number;
  showLabelsInTrack: boolean;
  glowOnActive: boolean;
}

interface ToggleSwitchProps {
  component: Component;
}

// ─── Theme Presets ───────────────────────────────────────────────────────────

const TOGGLE_THEMES: Record<string, ToggleThemeConfig> = {
  ios: {
    trackOnColor: '#34C759',
    trackOffColor: CONTROL_THEME.muted,
    thumbColor: '#ffffff',
    thumbShadow: '0 2px 4px rgba(0,0,0,0.2)',
    borderRadius: '999px',
    trackWidth: 51,
    trackHeight: 31,
    thumbSize: 27,
    thumbOffset: 2,
    showLabelsInTrack: false,
    glowOnActive: false,
  },
  material: {
    trackOnColor: '#6200EE',
    trackOffColor: CONTROL_THEME.muted,
    thumbColor: '#ffffff',
    thumbShadow: '0 2px 6px rgba(0,0,0,0.3)',
    borderRadius: '14px',
    trackWidth: 52,
    trackHeight: 28,
    thumbSize: 24,
    thumbOffset: 2,
    showLabelsInTrack: false,
    glowOnActive: false,
  },
  neon: {
    trackOnColor: '#00ff88',
    trackOffColor: '#1a1a2e',
    thumbColor: '#ffffff',
    thumbShadow: '0 0 10px rgba(0,255,136,0.6), 0 0 4px rgba(0,255,136,0.4)',
    borderRadius: '999px',
    trackWidth: 54,
    trackHeight: 30,
    thumbSize: 24,
    thumbOffset: 3,
    showLabelsInTrack: false,
    glowOnActive: true,
  },
  minimal: {
    trackOnColor: '#4A90D9',
    trackOffColor: CONTROL_THEME.muted,
    thumbColor: '#ffffff',
    thumbShadow: 'none',
    borderRadius: '999px',
    trackWidth: 40,
    trackHeight: 20,
    thumbSize: 16,
    thumbOffset: 2,
    showLabelsInTrack: false,
    glowOnActive: false,
  },
  pill: {
    trackOnColor: '#10B981',
    trackOffColor: CONTROL_THEME.muted,
    thumbColor: '#ffffff',
    thumbShadow: '0 1px 3px rgba(0,0,0,0.2)',
    borderRadius: '999px',
    trackWidth: 64,
    trackHeight: 32,
    thumbSize: 26,
    thumbOffset: 3,
    showLabelsInTrack: true,
    glowOnActive: false,
  },
};

// ─── Size Presets ────────────────────────────────────────────────────────────

const SIZE_SCALE: Record<string, number> = {
  sm: 0.75,
  md: 1,
  lg: 1.35,
};

// ─── Component ───────────────────────────────────────────────────────────────

export const ToggleSwitch: React.FC<ToggleSwitchProps> = ({ component }) => {
  const properties = (component.properties || {}) as Record<string, unknown>;

  // ─── Theme Resolution ────────────────────────────────────────────────
  const themeName = (properties.theme as string) ?? 'ios';
  const size = (properties.size as string) ?? 'md';
  const disabled = (properties.disabled as boolean) ?? false;
  const label = (properties.label as string) ?? '';
  const onLabel = (properties.onLabel as string) ?? 'ON';
  const offLabel = (properties.offLabel as string) ?? 'OFF';

  const theme = useMemo(() => {
    const base = TOGGLE_THEMES[themeName] ?? TOGGLE_THEMES['ios'];
    const merged = {
      ...base,
      ...(properties.onColor != null && { trackOnColor: properties.onColor as string }),
      ...(properties.offColor != null && { trackOffColor: properties.offColor as string }),
      ...(properties.thumbColor != null && { thumbColor: properties.thumbColor as string }),
    };
    // When neon glow is active and user overrode onColor, update thumb shadow to match
    if (merged.glowOnActive && properties.onColor != null) {
      merged.thumbShadow = `0 0 10px ${merged.trackOnColor}99, 0 0 4px ${merged.trackOnColor}66`;
    }
    return merged;
  }, [themeName, properties.onColor, properties.offColor, properties.thumbColor]);

  const borderColor = (properties.borderColor as string) ?? undefined;
  const trackBorderColor = (properties.trackBorderColor as string) ?? undefined;
  const scale = SIZE_SCALE[size] ?? 1;

  // ─── Data Binding ────────────────────────────────────────────────────
  const dataSource = properties.dataSource as ChartDataSource | undefined;
  const templateId = properties.queryTemplateId as string | undefined;
  const valueField = (properties.valueField as string) ?? 'value';

  const { value: rawValue } = useDataTextValue({
    dataSource,
    componentId: component.id,
    valueField,
    staticValue: dataSource ? undefined : undefined,
    templateId,
  });

  // ─── Interaction ─────────────────────────────────────────────────────
  const { handlers, mode } = useComponentInteraction(component);
  const isViewerMode = mode === 'functional';
  const sensorControlBlocked = shouldBlockSensorControl(component);

  const sensorId = useMemo(() => {
    const action = properties.action;
    if (typeof action === 'object' && action !== null) {
      return (action as Record<string, unknown>).deviceId as string || component.id;
    }
    return component.id;
  }, [properties.action, component.id]);

  // Check if a device is bound — if so, allow control in any mode
  const deviceBinding = useMemo(() => {
    const action = properties.action;
    if (typeof action === 'object' && action !== null) {
      return (action as Record<string, unknown>).deviceBinding as import('@/components/shared/rendering/InteractionContext').DeviceBinding | undefined;
    }
    return undefined;
  }, [properties.action]);

  // Poll device status when bound
  const deviceStatus = useDeviceStatus(deviceBinding);

  // Subscribe to live Home Assistant state when this toggle is HA-bound.
  // The hook is idempotent and returns null for non-HA components.
  const haLiveState = useHomeAssistantEntityState(component);
  const haIsOn = haLiveState ? haStateToBoolean(haLiveState.state) : null;

  // Determine isOn state: HA live state > device status > data source value > property
  const isOn = useMemo(() => {
    if (haIsOn !== null) return haIsOn;
    if (deviceStatus) return deviceStatus.isOn;
    if (dataSource && rawValue !== null) {
      return Boolean(rawValue) && rawValue !== 0 && rawValue !== '0' && rawValue !== 'false' && rawValue !== 'off';
    }
    return (properties.isOn as boolean) ?? true;
  }, [haIsOn, deviceStatus, dataSource, rawValue, properties.isOn]);

  const handleToggle = useCallback(async () => {
    if (disabled || sensorControlBlocked) return;

    // Device-bound: control physical device directly (works in editor + viewer)
    if (deviceBinding) {
      const { executeDeviceCommand } = await import('@/integrations/kasa/deviceCommandMapper');
      executeDeviceCommand(
        { sensorId, action: 'control', parameters: { command: 'set', property: 'isOn', value: !isOn } },
        deviceBinding
      );
      return;
    }

    // Non-bound: only works in viewer mode
    if (!isViewerMode) return;
    handlers.onSensorControl?.({
      sensorId,
      action: 'control',
      parameters: { command: 'set', property: 'isOn', value: !isOn },
    });
  }, [disabled, sensorControlBlocked, deviceBinding, isViewerMode, handlers, sensorId, isOn]);

  // ─── Dimensions ──────────────────────────────────────────────────────
  const trackW = theme.trackWidth * scale;
  const trackH = theme.trackHeight * scale;
  const thumbSz = theme.thumbSize * scale;
  const offset = theme.thumbOffset * scale;
  const thumbTranslateX = isOn ? trackW - thumbSz - offset : offset;

  // ─── Styles ──────────────────────────────────────────────────────────
  const trackStyle: React.CSSProperties = {
    width: trackW,
    height: trackH,
    borderRadius: theme.borderRadius,
    backgroundColor: isOn ? theme.trackOnColor : theme.trackOffColor,
    position: 'relative',
    cursor: isViewerMode && !disabled && !sensorControlBlocked ? 'pointer' : 'default',
    transition: 'background-color 0.25s ease',
    opacity: disabled ? 0.5 : 1,
    flexShrink: 0,
    ...(trackBorderColor ? { border: `2px solid ${trackBorderColor}` } : {}),
    ...(isOn && theme.glowOnActive ? {
      boxShadow: `0 0 12px ${theme.trackOnColor}80`,
    } : {}),
  };

  const thumbStyle: React.CSSProperties = {
    width: thumbSz,
    height: thumbSz,
    borderRadius: '50%',
    backgroundColor: theme.thumbColor,
    boxShadow: theme.thumbShadow,
    position: 'absolute',
    top: offset,
    left: 0,
    transform: `translateX(${thumbTranslateX}px)`,
    transition: 'transform 0.25s ease',
    ...(borderColor ? { border: `2px solid ${borderColor}` } : {}),
  };

  return (
    <div
      data-testid="toggle-switch"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        height: '100%',
        gap: 6 * scale,
        userSelect: 'none',
      }}
    >
      {/* Track */}
      <div
        role="switch"
        aria-checked={isOn}
        aria-disabled={disabled || sensorControlBlocked}
        aria-label={label || 'Toggle switch'}
        tabIndex={isViewerMode && !sensorControlBlocked ? 0 : -1}
        onClick={handleToggle}
        onKeyDown={(e) => {
          if ((e.key === 'Enter' || e.key === ' ') && isViewerMode && !sensorControlBlocked) {
            e.preventDefault();
            handleToggle();
          }
        }}
        style={trackStyle}
      >
        {/* In-track labels for pill theme */}
        {theme.showLabelsInTrack && (
          <>
            <span style={{
              position: 'absolute',
              left: 8 * scale,
              top: '50%',
              transform: 'translateY(-50%)',
              fontSize: 10 * scale,
              fontWeight: 600,
              color: isOn ? '#fff' : 'transparent',
              transition: 'color 0.2s ease',
              pointerEvents: 'none',
            }}>
              {onLabel}
            </span>
            <span style={{
              position: 'absolute',
              right: 8 * scale,
              top: '50%',
              transform: 'translateY(-50%)',
              fontSize: 10 * scale,
              fontWeight: 600,
              color: isOn ? 'transparent' : '#fff',
              transition: 'color 0.2s ease',
              pointerEvents: 'none',
            }}>
              {offLabel}
            </span>
          </>
        )}

        {/* Thumb */}
        <div data-testid="toggle-switch-thumb" style={thumbStyle} />
      </div>

    </div>
  );
};
